/**
 * Which payers each tab of the Payers list asks the server for (FE-93).
 *
 * The tabs are one list under different filters, so the filters ARE the difference between them — and both consoles
 * have to agree on what "halted" means. They live here, in one place, as plain specs rather than `ICustomFilter`s:
 * that type sits beside the data table's React components, and this module is tested in node under `test:services`.
 * Each console turns a spec into its own filter object.
 *
 * Jordan Kelley asked for the Halt tab on 2026-09-17; John Russell added `filterHalt` to `/payer`, `/payerprovider`
 * and `/provider` on 2026-09-21, which is what makes the tab a server-side list rather than a guess.
 */

/** The tabs of the Payers list. The provider console has no unconfigured queue, so it shows two of the three. */
export type PayerTab = 'enter' | 'unconfigured' | 'halt';

export interface IPayerTabFilter {
    /** The request filter to send. */
    filterId: 'filterName' | 'filterHalt';
    /** The comparator, by its name in `FilterMethod`. */
    method: 'LK' | 'EQ';
    value: string;
    /** What the table's filter chip calls it. Without one the chip reads the filter id back: "Filter Halt". */
    label?: string;
    /**
     * Whether the chip can be removed.
     *
     * A filter that IS the tab is locked: removing it would leave every payer listed under a tab that says they are
     * halted — a list that looks like an answer and is not one (NS-3).
     */
    locked?: boolean;
}

/**
 * What the Halt tab says above the list, in one place so the two consoles cannot tell an operator different things.
 *
 * It names no lane on purpose. Jordan's screenshot said "claims and eligibility traffic" and our own halt
 * confirmation says only *"Claims will stop going to this payer until it is resumed"* — and which lanes a halt
 * actually stops is a backend question nobody here had answered. Either version would have been an assertion about
 * routing that an operator acts on (NS-3); this one is true whichever way it works, and stays true if a halt also
 * covers attachments or status requests. Vadim chose it on 2026-09-22 over naming claims alone.
 */
export const HALTED_PAYERS_NOTICE = 'Nothing is sent to a halted payer until the halt is lifted.';

/** A halted payer sends no claims until the halt is lifted. `filterHalt` is how the server names them. */
export const haltedPayerFilter = (): IPayerTabFilter => ({
    filterId: 'filterHalt',
    method: 'EQ',
    value: '1',
    label: 'Halted',
    locked: true,
});

/**
 * Whether a list under these filters takes disabled payers as well as active ones.
 *
 * A halt is on the payer whether or not the payer is active, and the Halt tab lists every halted payer — the ask was
 * "all of the Payers in HALT state". Support adds `filterActive=EQ:1` to any list that does not carry one, which left a
 * halted but disabled payer out of the Halt rows and its count, while the provider console, which sends no such
 * default, listed it (Copilot caught the difference on #537). Vadim settled it on 2026-09-24: the tab includes them.
 *
 * Keyed to the Halt tab's own filter rather than to the tab, so the request says what the screen says — `payerTabHolds`
 * gives up the tab the moment that filter goes. Only the DEFAULT is skipped: an Active filter the operator set applies.
 */
export function includesInactivePayers(applied: ReadonlyArray<IAppliedPayerFilter>): boolean {
    const halted = haltedPayerFilter();
    return applied.some(filter => isTabFilter(filter, halted));
}

/**
 * A tab's count, read from the server's `pagination-total` header: a whole, non-negative number, or `undefined`.
 *
 * `parseInt` reads a prefix — `'3garbage'` is 3, `'-1'` is -1, `'2.5'` is 2 — so a damaged header came back as a
 * count that looks real, on a tab an operator reads as the size of a queue (NS-3; Copilot caught it on #537). No
 * number is honest about not knowing.
 */
export function tabCountFromHeader(header: string | null | undefined): number | undefined {
    const text = header?.trim();
    if (!text || !/^\d+$/.test(text)) {
        return undefined;
    }
    const count = Number(text);
    return Number.isSafeInteger(count) ? count : undefined;
}

/** One filter as the data table actually holds it, narrowed to the three fields a tab cares about. */
export interface IAppliedPayerFilter {
    /** Optional because the data table's own filter type says so — a row being built has no id yet, and cannot match. */
    filterId?: string;
    /** `FilterMethod`, which is a string enum — its values are the names, so a string compare is exact. */
    filterMethod?: string;
    value?: unknown;
}

/**
 * Whether what is on screen still satisfies the tab's own constraint.
 *
 * `locked` does NOT mean a filter cannot be changed. The shared model says so in as many words — *"If filter locked,
 * it is not possible to remove it, but still possible to change the value"* — and the Filters panel renders a full
 * editor for a locked row, hiding only its delete button. So an operator on the Halt tab can open Filter, change the
 * field from Halted to Name or the value from 1 to 0, and be left looking at unhalted payers under a heading that
 * says Halt: exactly the assertion about routing NS-3 forbids, made by the screen rather than by us.
 *
 * Each page therefore watches its filters and, when the tab's own constraint no longer holds, stops claiming the tab
 * — it falls back to the unfiltered one, which promises nothing, and leaves the operator's edit in place. Preferred
 * over snapping the filter back, which fights someone who meant it, and over forbidding the edit, which would mean
 * teaching every table in the product a new flag for one screen (Copilot caught this on #537).
 *
 * Holding takes both halves: every filter the tab asks for is there, AND nothing else sits on a field the tab owns.
 * The server ORs repeated values of one filter (FE-24), so a second `filterName` beside Unconfigured's — or a second
 * `filterHalt` beside Halt's, once the Filters panel offers that field — widens the list past the tab while the tab's
 * own filter is still on screen. Filters on other fields only narrow the list, so they never break a tab.
 */
export function payerTabHolds(
    tab: PayerTab,
    generatedPayerPrefix: string,
    applied: ReadonlyArray<IAppliedPayerFilter>,
): boolean {
    const specs = payerTabFilters(tab, generatedPayerPrefix);
    const onTabField = (filter: IAppliedPayerFilter): boolean => specs.some(spec => spec.filterId === filter.filterId);

    return (
        specs.every(spec => applied.some(filter => isTabFilter(filter, spec))) &&
        applied.filter(onTabField).every(filter => specs.some(spec => isTabFilter(filter, spec)))
    );
}

/**
 * What stays on screen when the list switches from one tab to another; the caller then adds the new tab's filters.
 *
 * Two things go. The old tab's own filters, exactly as the tab wrote them: a switch that only ADDED its filter would
 * keep the previous one's, and leaving Unconfigured for Halt would ask for "halted generated payers" — an all-but-empty
 * list that looks like an account with no halts. And anything on a field the new tab owns: beside the new tab's filter
 * the server would OR it in and widen the tab (FE-24), and `payerTabHolds` would take the tab straight back.
 *
 * Everything else is the operator's and stays, including their own filter on a field the OLD tab used — a Name filter
 * added on Halt survives the switch to ENTER payers. Clearing whole fields instead lost it (Copilot caught that on
 * #537). `alsoDrop` names anything else that leaves with the tab: Support's search, whose box empties on a switch.
 */
export function filtersKeptOnTabSwitch<T extends IAppliedPayerFilter>(
    from: PayerTab,
    to: PayerTab,
    generatedPayerPrefix: string,
    applied: ReadonlyArray<T>,
    alsoDrop: (filter: T) => boolean = (): boolean => false,
): T[] {
    const fromSpecs = payerTabFilters(from, generatedPayerPrefix);
    const toFields = payerTabFilters(to, generatedPayerPrefix).map(spec => spec.filterId as string);

    return applied.filter(
        filter =>
            !fromSpecs.some(spec => isTabFilter(filter, spec)) &&
            !(filter.filterId !== undefined && toFields.includes(filter.filterId)) &&
            !alsoDrop(filter),
    );
}

/** Whether `filter` is exactly the one `spec` asks for: the same field, comparator and value. */
function isTabFilter(filter: IAppliedPayerFilter, spec: IPayerTabFilter): boolean {
    return (
        filter.filterId === spec.filterId && String(filter.filterMethod) === spec.method && String(filter.value) === spec.value
    );
}

/**
 * The filters a tab adds, and nothing else.
 *
 * The Halt tab deliberately writes no `filterName`: the search box writes that one, the server ORs repeated values of
 * a single filter, and a second `filterName` would widen the list rather than narrow it (FE-24). Halt and search are
 * different filters, so they AND together and can both be on at once.
 */
export function payerTabFilters(tab: PayerTab, generatedPayerPrefix: string): IPayerTabFilter[] {
    if (tab === 'unconfigured') {
        return [{ filterId: 'filterName', method: 'LK', value: generatedPayerPrefix }];
    }
    if (tab === 'halt') {
        return [haltedPayerFilter()];
    }
    return [];
}
